import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, refreshLeadOptions, STAGE_LABEL, SOURCE_LABEL } from "@/lib/api";
import { ColumnMapper } from "@/components/import/ColumnMapper";
import { ValueReview } from "@/components/import/ValueReview";
import { CONSOLE } from "@/constants/testIds";
import { Switch } from "@/components/ui/switch";
import { UploadCloud, FileText, CheckCircle2, Info, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "name", label: "Name", required: true, alias: ["name", "full_name", "lead_name", "customer_name", "client_name"] },
  { key: "phone", label: "Phone", required: true, alias: ["phone", "mobile", "phone_number", "mobile_number", "contact", "contact_number", "whatsapp"] },
  { key: "email", label: "Email", alias: ["email", "email_id", "mail"] },
  { key: "source", label: "Source", alias: ["source", "lead_source", "channel", "platform", "source_name"] },
  { key: "project_name", label: "Project name", alias: ["project_name", "project", "property"] },
  { key: "budget_min", label: "Budget min", alias: ["budget_min", "min_budget", "budget"] },
  { key: "budget_max", label: "Budget max", alias: ["budget_max", "max_budget"] },
  { key: "configuration", label: "Configuration", alias: ["configuration", "config", "bhk", "unit_type"] },
  { key: "location_pref", label: "Location preference", alias: ["location_pref", "location", "preferred_location", "city"] },
  { key: "notes", label: "Notes", alias: ["notes", "note", "remarks", "comments", "comment"] },
  { key: "created_at", label: "Created date", alias: ["created_at", "created_date", "created", "created_on", "lead_date", "date"] },
  { key: "updated_at", label: "Updated date", alias: ["updated_at", "updated_date", "updated", "modified", "last_updated", "modified_on"] },
  { key: "owner", label: "Lead owner", alias: ["owner", "lead_owner", "assigned_to", "executive", "sales_person", "agent"] },
  { key: "status", label: "Lead status", alias: ["status", "lead_status", "stage"] },
  { key: "followup_date", label: "Follow-up date", alias: ["followup_date", "follow_up_date", "followup", "follow_up", "next_followup", "next_follow_up", "next_follow_up_date", "next_followup_date", "follow_up_on", "next_call_date", "callback_date"] },
  { key: "due_date", label: "Due date", alias: ["due_date", "due", "due_on", "deadline"] },
];
const BLANK = new Set(["", "na", "n/a", "-", "--", "nil", "none", "null"]);
const CHUNK = 300;
const norm = (h) => h.replace(/^\uFEFF/, "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
const val = (v) => (v == null || BLANK.has(String(v).trim().toLowerCase()) ? null : String(v).trim());
const money = (v) => { const s = val(v); if (!s) return null; const n = parseFloat(s.replace(/[^\d.]/g, "")); if (!Number.isFinite(n)) return null; return /cr/i.test(s) ? n * 1e7 : /l(ac|akh)?\b/i.test(s) ? n * 1e5 : n; };

function parseCsv(text) {
  const rows = []; let row = []; let cur = ""; let q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += c;
    } else if (c === '"') q = true;
    else if (c === ",") { row.push(cur.trim()); cur = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(cur.trim()); cur = "";
      if (row.some((x) => x)) rows.push(row);
      row = [];
    } else cur += c;
  }
  row.push(cur.trim());
  if (row.some((x) => x)) rows.push(row);
  if (!rows.length) return { headers: [], rows: [] };
  return { headers: rows[0].map((h) => h.replace(/^\uFEFF/, "")), rows: rows.slice(1) };
}

const guess = (headers) => Object.fromEntries(FIELDS.map((f) => [f.key, headers.findIndex((h) => f.alias.includes(norm(h)))]).map(([k, i]) => [k, i >= 0 ? String(i) : ""]));

const Instructions = () => (
  <div data-testid="import-instructions" className="border border-[#E6E4DD] bg-white rounded-sm p-5 text-sm text-forest/80 space-y-2">
    <div className="font-display font-bold text-forest flex items-center gap-2"><Info className="h-4 w-4" /> How to prepare your file</div>
    <ul className="list-disc pl-5 space-y-1 text-[13px]">
      <li>Save your sheet as <b>CSV</b>. The first row must be the column headers — any header names work, you match them below.</li>
      <li><b>Name</b> and <b>Phone</b> are required. Every other column can be left blank or filled with <b>NA</b>.</li>
      <li>Leads whose phone number is already in Propzel (or repeated in the file) are <b>not created again</b> — but their follow-up / due date is added to the existing lead.</li>
      <li><b>Smart matching</b>: AI reads your headers and sample rows and picks the Propzel field for each column — change any pick in the live preview.</li>
      <li><b>Lead status, source and owner</b>: each distinct value in the file is matched to the closest Propzel stage, source or team member (typos and short names are fine). Values with no close match (e.g. Junk) can be added as a new stage or source for this organisation.</li>
      <li><b>Notes</b> are saved on the lead and added to its timeline.</li>
      <li><b>Dates</b> (created, updated, follow-up): DD/MM/YYYY, YYYY-MM-DD or with time like 25/06/2026 3:00 PM. A follow-up or due date creates a call task for the lead owner (10:00 AM if no time). Unreadable dates are flagged in the summary. Leads without a follow-up date get the automatic follow-up call task (if switched on in Settings).</li>
      <li><b>Source</b>: FB, Meta, Facebook Ads → Facebook; IG, Instagram Ads → Instagram (also read from Platform / Source name columns).</li>
      <li><b>Budget</b>: plain numbers or values like 45 L / 1.2 Cr. <b>Project name</b> must match a project in Propzel, otherwise the lead goes to the unassigned project list.</li>
    </ul>
  </div>
);

const distinct = (rows, idx) => {
  if (idx === "" || idx == null) return [];
  const c = new Map();
  rows.forEach((r) => { const v = val(r[Number(idx)]); if (v) c.set(v, (c.get(v) || 0) + 1); });
  return [...c.entries()].sort((x, y) => y[1] - x[1]).slice(0, 300);
};

export default function DataImport() {
  const [file, setFile] = useState({ headers: [], rows: [] });
  const [map, setMap] = useState({});
  const [fileName, setFileName] = useState("");
  const [autoAssign, setAutoAssign] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [reasons, setReasons] = useState({});
  const [aiState, setAiState] = useState("");
  const [users, setUsers] = useState([]);
  const [valueMap, setValueMap] = useState({ stages: {}, sources: {}, owners: {} });
  const [valState, setValState] = useState("");
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active !== false))).catch(() => {}); }, []);

  const values = useMemo(() => ({ stages: distinct(file.rows, map.status), sources: distinct(file.rows, map.source), owners: distinct(file.rows, map.owner) }),
    [file.rows, map.status, map.source, map.owner]);
  const valuesKey = JSON.stringify([values.stages.map((v) => v[0]), values.sources.map((v) => v[0]), values.owners.map((v) => v[0])]);
  useEffect(() => {
    if (!values.stages.length && !values.sources.length && !values.owners.length) return undefined;
    setValState("loading");
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/leads/import/analyze-values", { statuses: values.stages.map((v) => v[0]), sources: values.sources.map((v) => v[0]), owners: values.owners.map((v) => v[0]) }, { timeout: 90000 });
        setValueMap({ stages: data.stages || {}, sources: data.sources || {}, owners: Object.fromEntries(Object.entries(data.owners || {}).map(([k, v]) => [k, v || ""])) });
        setValState(data.by);
      } catch { setValState("rules"); }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesKey]);

  const analyzeColumns = async (p, ruleMap) => {
    setAiState("loading"); setReasons({});
    try {
      const { data } = await api.post("/leads/import/analyze-columns", { headers: p.headers, samples: p.rows.slice(0, 5), rule_map: ruleMap }, { timeout: 90000 });
      setMap(data.map); setReasons(data.reasons || {}); setAiState(data.by);
    } catch { setAiState("rules"); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name); setResult(null);
    const reader = new FileReader();
    reader.onload = () => { const p = parseCsv(String(reader.result)); const g = guess(p.headers); setFile(p); setMap(g); setValueMap({ stages: {}, sources: {}, owners: {} }); if (p.headers.length) analyzeColumns(p, g); };
    reader.readAsText(f);
    e.target.value = "";
  };

  const mapped = useMemo(() => file.rows.map((cells) => {
    const g = (k) => (map[k] === "" || map[k] == null ? null : val(cells[Number(map[k])]));
    return {
      name: g("name") || "", phone: g("phone"), email: g("email"), source: g("source") || "manual", project_name: g("project_name"),
      budget_min: money(g("budget_min")), budget_max: money(g("budget_max")), configuration: g("configuration"), location_pref: g("location_pref"),
      notes: g("notes"), created_at: g("created_at"), updated_at: g("updated_at"), owner: g("owner"), status: g("status"), followup_date: g("followup_date"), due_date: g("due_date"),
    };
  }), [file, map]);
  const ready = map.name !== "" && map.phone !== "" && file.rows.length > 0;

  const createNew = async (kind, group) => {
    const out = { ...valueMap[group] };
    for (const [raw, v] of Object.entries(out)) {
      if (v === "__new__") out[raw] = (await api.post("/lead-options", { kind, label: raw.slice(0, 40) })).data.key;
    }
    return out;
  };

  const runImport = async () => {
    setBusy(true); setProgress(0);
    let maps;
    try {
      maps = { stage_map: await createNew("stage", "stages"), source_map: await createNew("source", "sources"), owner_map: Object.fromEntries(Object.entries(valueMap.owners).filter(([, v]) => v)) };
      await refreshLeadOptions();
      setValueMap((p) => ({ ...p, stages: maps.stage_map, sources: maps.source_map }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create new stages/sources"); setBusy(false); return; }
    const total = { created: 0, skipped: 0, failed: 0, followups: 0, followups_on_existing: 0, date_warnings: 0, notes: 0, errors: [] };
    const post = async (rows, start) => {
      for (let attempt = 0; ; attempt++) {
        try { return (await api.post("/leads/import", { rows, auto_assign: autoAssign, start_row: start, ...maps }, { timeout: 120000 })).data; }
        catch (e) {
          const s = e.response?.status;
          if (attempt < 2 && (!s || s >= 500)) { await new Promise((r) => setTimeout(r, 1500 * (attempt + 1))); continue; }
          throw e;
        }
      }
    };
    for (let i = 0; i < mapped.length; i += CHUNK) {
      const rows = mapped.slice(i, i + CHUNK);
      try {
        const data = await post(rows, i + 2);
        ["created", "skipped", "failed", "followups", "followups_on_existing", "date_warnings", "notes"].forEach((k) => { total[k] += data[k] || 0; });
        total.errors.push(...data.errors);
      } catch (e) {
        total.failed += rows.length;
        const d = e.response?.data?.detail;
        total.errors.push({ row: `${i + 2}–${i + 1 + rows.length}`, reason: d ? formatApiError(d) : `Batch failed (${e.response?.status || "network error"})` });
      }
      setProgress(Math.min(mapped.length, i + CHUNK));
      setResult({ ...total, errors: [...total.errors] });
    }
    setBusy(false);
    toast.success(`Imported ${total.created} leads · ${total.skipped} duplicates skipped${total.failed ? ` · ${total.failed} failed` : ""}`);
  };

  const sampleCsv = "Name,Phone,Email,Source,Project,Budget,Configuration,Location,Notes,Created Date,Updated Date,Lead Owner,Lead Status,Followup Date\nAsha Kumar,9812345678,asha@example.com,website,Aurelia Heights,45 L,3BHK,Whitefield,Wants east facing,01/06/2026,05/06/2026,Priya Sharma,Contacted,28/06/2026 11:00 AM\nRohit Sen,9823456789,NA,magicbricks,NA,NA,NA,NA,NA,NA,NA,NA,New,NA";
  const preview = mapped.slice(0, 8);
  const shown = FIELDS.filter((f) => map[f.key] !== "" && map[f.key] != null);
  const shownValue = (k, v) => {
    if (v == null) return null;
    const lookup = (m, labels) => (m[v] === "__new__" ? `${v} (new)` : labels[m[v]] || v);
    if (k === "status") return lookup(valueMap.stages, STAGE_LABEL);
    if (k === "source") return lookup(valueMap.sources, SOURCE_LABEL);
    if (k === "owner" && v in valueMap.owners) return users.find((u) => u.id === valueMap.owners[v])?.name || "Admin's bucket";
    return v;
  };

  return (
    <div className="space-y-6 max-w-6xl min-w-0">
      <div>
        <div className="label-caps">Console</div>
        <h2 className="font-display font-black text-3xl text-forest tracking-tight mt-1">Data import</h2>
        <div className="text-sm text-forest/60 mt-1">Upload a CSV, match its columns to Propzel fields and import leads in bulk.</div>
      </div>
      <Instructions />
      <label className="block border-2 border-dashed border-[#E6E4DD] rounded-sm p-10 text-center hover:border-forest transition-colors duration-150 cursor-pointer bg-white">
        <input data-testid={CONSOLE.importFile} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
        <UploadCloud className="h-8 w-8 mx-auto text-forest/60 mb-3" />
        <div className="text-sm text-forest font-medium">{fileName || "Drop a CSV or click to browse"}</div>
        <div className="text-xs text-forest/50 mt-1">{file.rows.length ? `${file.rows.length} rows · ${file.headers.length} columns detected` : "First row = column headers"}</div>
      </label>
      {file.headers.length > 0 && <ColumnMapper headers={file.headers} rows={file.rows} map={map} setMap={setMap} reasons={reasons} aiState={aiState} />}
      {file.headers.length > 0 && <ValueReview values={values} valueMap={valueMap} setValueMap={setValueMap} users={users} state={valState} />}
      <div className="flex items-center gap-3 border border-[#E6E4DD] bg-white rounded-sm p-4">
        <Switch data-testid="import-auto-assign" checked={autoAssign} onCheckedChange={setAutoAssign} />
        <div className="text-sm text-forest flex-1">When Lead owner is empty, auto-assign to executives (least-loaded first). Off = goes to the admin's bucket.</div>
      </div>
      <div className="flex justify-between items-center">
        <button data-testid="import-copy-sample" onClick={() => { navigator.clipboard.writeText(sampleCsv); toast.success("Sample copied"); }} className="text-xs uppercase tracking-[0.18em] font-bold text-forest hover:text-clay transition-colors duration-150 inline-flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Copy sample CSV</button>
        <button data-testid={CONSOLE.importBtn} onClick={runImport} disabled={busy || !ready || aiState === "loading" || valState === "loading"} className="h-10 px-5 rounded-sm bg-forest text-white text-sm font-medium hover:bg-forest-soft transition-colors duration-150 disabled:opacity-60">
          {busy ? `Importing… ${progress}/${mapped.length}` : `Import ${file.rows.length} rows`}
        </button>
      </div>
      {result && (
        <div data-testid="import-result" className="border border-[#2D6A4F]/30 bg-[#2D6A4F]/5 text-forest rounded-sm p-4 space-y-2">
          <div className="flex items-center gap-3 text-sm"><CheckCircle2 className="h-4 w-4 text-[#2D6A4F]" /><span><b className="text-[#2D6A4F]">{result.created} leads</b> imported · {result.skipped} duplicates skipped · {result.failed} failed · {result.followups} follow-ups created · {result.notes} notes added to timelines{result.followups_on_existing ? ` (${result.followups_on_existing} on existing leads)` : ""}{result.date_warnings ? ` · ${result.date_warnings} unreadable dates flagged` : ""}</span></div>
          {result.errors.length > 0 && <div className="text-xs text-forest/70 space-y-0.5 max-h-40 overflow-y-auto">{result.errors.map((e, i) => <div key={i} className="flex gap-1.5"><AlertTriangle className="h-3 w-3 text-clay mt-0.5" />Row {e.row}: {e.reason}</div>)}</div>}
        </div>
      )}
      {preview.length > 0 && shown.length > 0 && (
        <div className="border border-[#E6E4DD] bg-white rounded-sm overflow-x-auto" data-testid="import-preview">
          <div className="px-3 py-2 label-caps border-b border-[#E6E4DD]">Preview (as it will be imported)</div>
          <table className="w-full text-xs">
            <thead className="bg-bone-alt/60 border-b border-[#E6E4DD]"><tr className="text-[10px] uppercase tracking-[0.15em] text-forest/70">{shown.map((f) => <th key={f.key} className="text-left px-3 py-2 font-bold whitespace-nowrap">{f.label}</th>)}</tr></thead>
            <tbody className="divide-y divide-[#E6E4DD]">
              {preview.map((r, i) => <tr key={i}>{shown.map((f) => <td key={f.key} className="px-3 py-2 text-forest/70 truncate max-w-[180px]">{shownValue(f.key, r[f.key]) ?? <span className="text-forest/30">NA</span>}</td>)}</tr>)}
            </tbody>
          </table>
          {mapped.length > preview.length && <div className="px-3 py-2 text-[10px] text-forest/50 border-t border-[#E6E4DD]">…and {mapped.length - preview.length} more rows</div>}
        </div>
      )}
    </div>
  );
}
